import { IPromise } from 'angular';
import { WebsocketService } from '../../../core/services/websocket/websocket.service';

import { ChatDialog, IChatDialog } from '../models/chat-dialog';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ChatMessage } from '../models/chat-message';
import { IDataMap } from '../../../core/interfaces/data-map.interface';
import { UserService } from '../../../core/services/user/user.service';
import { ApiService } from '../../../core/services/api-service/api.service';
import { EmployerService } from '../../../core/services/employer/employer.service';
import { CvService } from '../../../core/services/cv.service';

import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { merge } from 'rxjs/observable/merge';
import { IChatFile } from '../components';
import * as moment from 'moment';
import { concat } from '../../../../../node_modules/rxjs/operator/concat';

export enum ChatEvents {
  GET_DIALOGS = 'chat get dialogs',
  DIALOGS_LOADED = 'chat dialogs loaded',
  GET_DIALOG_MESSAGES = 'chat get dialog messages',
  DIALOG_MESSAGES_LOADED = 'dialog messages loaded',
  DIALOG_MESSAGES_UPDATED = 'dialog messages updated',
  MESSAGE = 'chat message',
  MESSAGE_LOADED = 'chat message loaded',
  CHAT_ERROR = 'backend error',
  ERROR_EXIST = 'error exist',
  MESSAGE_SENT = 'success',
  ONLINE_STATUS = 'chat online status',
  STATUS_UPDATED = 'chat status updated',
  UNREAD_MESSAGES = 'chat unread_messages'
}

export interface ChatEvent<T> {
  type: string;
  data: T;
  pages?: number;
}

export interface IGetDialogMessagesReq {
  page?: number;
  limit?: number;
  id?: string;
}

export interface UploadEvent {
  type: 'loading' | 'new' | 'uploading';
  resolved: boolean;
}

export interface IMessageSendData {
  to: string;
  message?: string;
  file?: IChatFile;
}

export interface IUserStatus {
  user: string;
  status: number;
}

export interface IChatErrorRes {
  text: string;
}

export class ChatService {
  static $inject = [
    'WebsocketService',
    'AuthService',
    'UserService',
    'ApiService',
    'CvService',
    'EmployerService'
  ];
  usersStatus: IDataMap<number> = {};
  onLoad: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  onUpLoad: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  shouldUpload: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  scrollEvent: BehaviorSubject<boolean> = new BehaviorSubject(false);
  selectedDialog: BehaviorSubject<string> = new BehaviorSubject<string>(
    undefined
  );

  private shouldUpdate: boolean = false;
  private updatedDialogs: BehaviorSubject<ChatDialog[]> = new BehaviorSubject<
    ChatDialog[]
  >(undefined);
  private sentMessage: ChatMessage;

  constructor(
    private websocketService: WebsocketService,
    private authService: AuthService,
    private userService: UserService,
    private apiService: ApiService,
    private cvService: CvService,
    private employerService: EmployerService
  ) {}

  chatEvents(): Observable<ChatEvent<any>> {
    return merge(
      this.onDialogs(),
      this.onMessages(),
      this.onMessage(),
      this.onMessageSent(),
      this.onUserStatus(),
      this.onChatError()
    );
  }

  // Chat actions;
  getDialogs() {
    this.websocketService.emit(ChatEvents.GET_DIALOGS);
  }

  updateDialogs(dialogs: ChatDialog[]) {
    this.updatedDialogs.next(dialogs);
  }

  getMessages(data: IGetDialogMessagesReq) {
    this.shouldUpdate = false;
    setTimeout(() => {
      this.getUnread();
    }, 200);
    this.websocketService.emit(ChatEvents.GET_DIALOG_MESSAGES, data);
  }

  getUnread() {
    this.websocketService.emit(ChatEvents.UNREAD_MESSAGES);
  }

  updateMessages(data: IGetDialogMessagesReq) {
    this.shouldUpdate = true;
    this.websocketService.emit(ChatEvents.GET_DIALOG_MESSAGES, data);
  }

  sendMessage(data: IMessageSendData, newMessage: ChatMessage) {
    this.sentMessage = newMessage;
    this.websocketService.emit(ChatEvents.MESSAGE, data);
  }

  getUserStatus(userId: string): number {
    if (userId === this.authService.user._id) {
      return 1;
    }
    return this.usersStatus[userId];
  }

  addToImportant(userId: string): IPromise<any> {
    return this.apiService
      .post('/user/addToImportant', { id: userId })
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  }

  deleteFromImportant(userId: string): IPromise<any> {
    return this.apiService
      .post('/user/deleteFromImportant', { id: userId })
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  }

  banUser(userId: string): IPromise<any> {
    return this.apiService
      .post('/user/addToBanned', { id: userId })
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  }

  unbanUser(userId: string): IPromise<any> {
    return this.apiService
      .post('/user/deleteFromBanned', { id: userId })
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  }

  // Watchers;

  onUnreadMessages(): Observable<any> {
    return new Observable((obs) => {
      this.websocketService.on(ChatEvents.UNREAD_MESSAGES, (data) => {
        obs.next(data.text.message);
      });
    });
  }

  onDialogs(): Observable<ChatEvent<ChatDialog[]>> {
    return merge(
      new Observable((obs) => {
        this.websocketService.on(
          ChatEvents.GET_DIALOGS,
          (data: { dialogs: IChatDialog[]; important: any[] }) => {
            obs.next(data.dialogs.map((value) => new ChatDialog(value)));
          }
        );
      }),
      this.updatedDialogs.filter((value) => !!value)
    ).pipe(
      map((res: ChatDialog[]) => {
        return res.sort((a, b) =>
          moment(b.last_message.date).diff(moment(a.last_message.date), 'ms')
        );
      }),
      switchMap((res) => {
        const chatEvent: ChatEvent<ChatDialog[]> = {
          type: ChatEvents.DIALOGS_LOADED,
          data: res
        };
        return of(chatEvent);
      })
    );
  }

  onMessages(): Observable<ChatEvent<ChatMessage[]>> {
    return new Observable<any>((obs) => {
      this.websocketService.on(ChatEvents.GET_DIALOG_MESSAGES, (data) => {
        obs.next(data);
      });
    }).pipe(
      map((res) => {
        return {
          to: res.to,
          pages: res.pages,
          messages: res.messages.map((value) => new ChatMessage(value))
        };
      }),
      switchMap((res) =>
        of<ChatEvent<ChatMessage[]>>({
          type: this.shouldUpdate
            ? ChatEvents.DIALOG_MESSAGES_UPDATED
            : ChatEvents.DIALOG_MESSAGES_LOADED,
          data: res.messages,
          pages: res.pages
        })
      )
    );
  }

  onMessage(): Observable<ChatEvent<ChatMessage>> {
    return new Observable<any>((obs) => {
      this.websocketService.on(ChatEvents.MESSAGE, (data) => {
        obs.next(new ChatMessage(data));
      });
    }).pipe(
      switchMap((res: ChatMessage) =>
        of<ChatEvent<ChatMessage>>({
          type: ChatEvents.MESSAGE_LOADED,
          data: res
        })
      )
    );
  }

  onMessageSent(): Observable<ChatEvent<ChatMessage>> {
    return new Observable<any>((obs) => {
      this.websocketService.on(ChatEvents.MESSAGE_SENT, (data) => {
        obs.next(data);
      });
    }).pipe(
      map(() => {
        return this.sentMessage;
      }),
      switchMap((res: ChatMessage) =>
        of<ChatEvent<ChatMessage>>({
          type: ChatEvents.MESSAGE_SENT,
          data: res
        })
      )
    );
  }

  onUserStatus(): Observable<ChatEvent<IUserStatus>> {
    return new Observable<any>((obs) => {
      this.websocketService.on(
        ChatEvents.ONLINE_STATUS,
        (data: IUserStatus) => {
          obs.next(data);
        }
      );
    }).pipe(
      switchMap((res: IUserStatus) =>
        of<ChatEvent<IUserStatus>>({
          type: ChatEvents.STATUS_UPDATED,
          data: res
        })
      )
    );
  }

  onChatError(): Observable<ChatEvent<IChatErrorRes>> {
    return new Observable<any>((obs) => {
      this.websocketService.on(ChatEvents.CHAT_ERROR, (data: IChatErrorRes) => {
        obs.next(data);
      });
    }).pipe(
      switchMap((res: IChatErrorRes) =>
        of<ChatEvent<IChatErrorRes>>({
          type: ChatEvents.ERROR_EXIST,
          data: res
        })
      )
    );
  }
}



// WEBPACK FOOTER //
// ./app/states/chat/services/chat-service.ts